import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useSubmissionsOpen } from "../hooks/useSubmissionsOpen";
import { Check, Heart, Users, Gift, ArrowRight, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultMembershipBenefits = [
  "Access to all public content",
  "Community forum access",
  "Submit your films to the showcase",
  "Post and browse job opportunities",
  "Member directory listing",
  "Monthly newsletter",
  "Event notifications",
  "Workshop access"
];

const volunteerRoles = [
  "Event Coordination",
  "Content Writing",
  "Social Media",
  "Community Moderation",
  "Technical Support",
  "Festival Coverage"
];

const donationPackages = [
  { id: "small", amount: 10 },
  { id: "medium", amount: 25 },
  { id: "large", amount: 50 },
  { id: "xl", amount: 100 }
];

export default function Join() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [donationAmount, setDonationAmount] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [membershipBenefits, setMembershipBenefits] = useState(defaultMembershipBenefits);
  const submissionsOpen = useSubmissionsOpen();
  const [content, setContent] = useState({
    header: {
      headline: "Become a Member",
      subhead: "Join the global community of AI filmmakers. The Society is a network of pioneers, creators, and storytellers defining the next era of cinema."
    },
    membership_card: {
      title: "Society Membership",
      subtitle: "Open Access",
      description: "Help define the future of film.",
      button_text: "Become a Member"
    }
  });

  // Fetch custom content from CMS
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [headerRes, cardRes, benefitsRes] = await Promise.all([
          axios.get(`${API}/site-content/join/header`),
          axios.get(`${API}/site-content/join/membership_card`),
          axios.get(`${API}/site-content/join/benefits_list`)
        ]);
        if (headerRes.data.content && Object.keys(headerRes.data.content).length > 0) {
          setContent(prev => ({ ...prev, header: { ...prev.header, ...headerRes.data.content } }));
        }
        if (cardRes.data.content && Object.keys(cardRes.data.content).length > 0) {
          setContent(prev => ({ ...prev, membership_card: { ...prev.membership_card, ...cardRes.data.content } }));
        }
        if (benefitsRes.data.content?.benefits && benefitsRes.data.content.benefits.length > 0) {
          setMembershipBenefits(benefitsRes.data.content.benefits);
        }
      } catch (error) {
        // Use defaults if CMS content not available
      }
    };
    fetchContent();
  }, []);

  // Check for donation success/cancel from Stripe redirect
  useEffect(() => {
    const donationStatus = searchParams.get("donation");
    const sessionId = searchParams.get("session_id");
    
    if (donationStatus === "success" && sessionId) {
      // Verify payment status
      checkPaymentStatus(sessionId);
    } else if (donationStatus === "cancelled") {
      toast.info("Donation cancelled. No payment was made.");
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/payments/donate/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success(`Thank you for your $${response.data.amount} donation! 🎉`);
      } else {
        toast.info("Payment is being processed. Thank you!");
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg.id);
    setDonationAmount(pkg.amount.toString());
  };

  const handleDonate = async () => {
    if (!donationAmount || isNaN(parseFloat(donationAmount)) || parseFloat(donationAmount) < 1) {
      toast.error("Please enter a valid donation amount (minimum $1)");
      return;
    }

    setIsProcessing(true);
    
    try {
      // Determine package or custom
      const amount = parseFloat(donationAmount);
      let packageId = "custom";
      const matchingPkg = donationPackages.find(p => p.amount === amount);
      if (matchingPkg) {
        packageId = matchingPkg.id;
      }

      const response = await axios.post(`${API}/payments/donate/checkout`, {
        package_id: packageId,
        custom_amount: packageId === "custom" ? amount : null,
        origin_url: window.location.origin
      }, { withCredentials: true });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Donation error:", error);
      toast.error(error.response?.data?.detail || "Failed to process donation");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Join" 
        description="Join the AI Film Society for free. Access workshops, submit films, connect with filmmakers, and support the future of AI cinema."
      />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Join the Community
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            {content.header.headline}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            {content.header.subhead}
          </p>
        </motion.div>

        {/* Free Membership */}
        <section className="mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div className="p-8 bg-void-surface border border-purple/30">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-purple/20 flex items-center justify-center">
                  <Users className="w-7 h-7 text-purple-light" />
                </div>
                <div>
                  <h2 className="font-syne font-bold text-white text-2xl">{content.membership_card.title}</h2>
                  <p className="text-purple-light text-lg font-bold">{content.membership_card.subtitle}</p>
                </div>
              </div>
              
              <p className="text-slate-300 mb-6">
                {content.membership_card.description}
              </p>
              
              <ul className="grid sm:grid-cols-2 gap-3 mb-8">
                {membershipBenefits
                  .filter((b) => submissionsOpen !== false || !/submit.*film/i.test(b))
                  .map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-purple-light flex-shrink-0 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>
              
              {user ? (
                <div className="text-center py-4 bg-purple/10 border border-purple/30">
                  <p className="text-purple-light font-medium">You&apos;re already a member!</p>
                </div>
              ) : (
                <Link
                  to="/register"
                  data-testid="join-free-btn"
                  className="block w-full py-4 bg-purple text-white text-center font-bold uppercase tracking-widest text-sm hover:bg-purple-hover transition-all"
                >
                  {content.membership_card.button_text}
                </Link>
              )}
            </div>
          </motion.div>
        </section>

        {/* Donate */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="p-8 md:p-12 bg-void-highlight border border-white/5">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-6 h-6 text-purple-light" />
              <h2 className="font-syne text-2xl font-bold text-white">
                Make a Donation
              </h2>
            </div>
            <p className="text-slate-300 mb-8 max-w-xl">
              Support the AI Film Society with a one-time donation. Your contribution 
              helps us host events, maintain resources, and grow the community.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex gap-2">
                {donationPackages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => handleSelectPackage(pkg)}
                    data-testid={`donate-${pkg.amount}`}
                    className={`px-4 py-2 text-sm transition-all ${
                      selectedPackage === pkg.id
                        ? "bg-purple text-white"
                        : "bg-void border border-white/20 text-white hover:border-purple/50"
                    }`}
                  >
                    ${pkg.amount}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">$</span>
                <input
                  type="number"
                  min="1"
                  value={donationAmount}
                  onChange={(e) => {
                    setDonationAmount(e.target.value);
                    setSelectedPackage(null);
                  }}
                  placeholder="Custom"
                  data-testid="custom-donation"
                  className="w-24 px-3 py-2 bg-void border border-white/20 text-white placeholder-slate-500 focus:border-purple/50 outline-none"
                />
              </div>
              <button
                onClick={handleDonate}
                disabled={isProcessing}
                data-testid="donate-btn"
                className="px-6 py-2 bg-purple text-white font-bold uppercase tracking-widest text-xs hover:bg-purple-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Donate"
                )}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              Secure payment powered by Stripe. You&apos;ll be redirected to complete your donation.
            </p>
          </div>
        </motion.section>

        {/* Sponsor */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="p-8 md:p-12 bg-void-surface border border-purple/20">
            <div className="flex items-center gap-3 mb-6">
              <Gift className="w-6 h-6 text-purple-light" />
              <h2 className="font-syne text-2xl font-bold text-white">
                Become a Sponsor
              </h2>
            </div>
            <p className="text-slate-300 mb-6 max-w-xl">
              Partner with the AI Film Society to reach a global audience of 
              AI filmmakers and enthusiasts. Sponsorship opportunities include 
              workshops, festivals, and exclusive events.
            </p>
            <a
              href="mailto:sam@aiadstudio.com?subject=AI Film Society Sponsorship Inquiry"
              data-testid="sponsor-contact"
              className="inline-flex items-center gap-2 text-purple-light hover:text-white transition-colors"
            >
              <Mail className="w-4 h-4" />
              Contact us for sponsorship details
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.section>

        {/* Volunteer */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="font-syne text-2xl font-bold text-white mb-6">
            Volunteer Opportunities
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl">
            Contribute your skills and time to help the community grow. 
            We&apos;re always looking for passionate volunteers.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {volunteerRoles.map((role, index) => (
              <motion.div
                key={role}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                data-testid={`volunteer-${index}`}
                className="p-4 bg-void-surface border border-white/5"
              >
                <p className="text-white">{role}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-slate-400 mt-6">
            Interested? <Link to="/forum" className="text-purple-light hover:text-white">Join our forum</Link> and introduce yourself!
          </p>
        </motion.section>
      </div>
    </div>
  );
}
